/**
 * Single-module store for the web planner's shared mutable state.
 *
 * All module-global `let` bindings that previously lived at the top of
 * main.ts are collected here as properties of one `state` object.
 * Consumers read via `state.X` and write via `state.X = ...`; readers
 * always see the current value because JS object properties are live.
 *
 * No pub/sub layer yet — renders are still triggered by explicit calls
 * from main.ts. A Phase 5b upgrade can wrap this in a Proxy to add
 * onChange subscriptions without touching call sites.
 *
 * Extracted from main.ts as part of OMON-031 Phase 5.
 */

import type { Chart } from "chart.js";
import type { Unsubscribe } from "firebase/firestore";
import type { ProvenanceFilter } from "./catalogue/provenance-view";
import type {
  ObservationDefinition,
  ActivityDefinition,
  GoalCategoryNode,
  GoalSubcategoryNode,
  PatientInfo,
  CarePlanInfo,
  GoalInfo,
  MonitoredEventInfo,
  BuilderMonitoredEvent,
  SelectedGoal,
  ActualObservation,
  NarrativeEntry,
  ClientContextData,
} from "./types";
import type { AuthClaims } from "./admin/claims";

/**
 * BuilderMode discriminates the plan-builder's UI state.
 *
 * - "view"            — patient detail, no edit in progress
 * - "create" / "edit" — plan builder form (edit-existing vs create-new)
 * - "pick"            — picker for developmental goals
 */
export type BuilderMode = "view" | "create" | "pick" | "edit";
export type CatalogueDetailTab = "attainment" | "prompts";
/** Zammad #49088: the M capability's tabs — kept apart from the D tabs' state. */
export type CatalogueMonitoredTab = "prompt" | "guidance";

/**
 * OMON-121: which view is active on the patient-detail screen. The
 * "developmental" tab shows SOMA goals with trajectory charts; the
 * "tasks" tab shows monitored events (no chart for now, modalities
 * still being settled). Default is "developmental" since that's the
 * majority-historical surface.
 */
// OMON-205 f/u: "dailylog" is promoted out of the monitored-event set — it is
// keyed on _recordingKind === "diary", never on the daily-log doc id, so a
// re-created OD (the current one already carries _migratedFrom) or a second
// diary trackable both keep working.
export type DetailActiveTab = "developmental" | "tasks" | "dailylog" | "narrative";

export interface EditingDetail {
  title: string;
  smartGoal: string;
  /** OMON-174: canonical monitored-event prompt (`_monitoredEventPrompt`) — the M capability's content, as SMART goal is D's. */
  monitoredEventPrompt: string;
  /** Zammad #49088: monitored-event carer guidance (`_monitoredEventGuidance`) — one markdown text per event. */
  monitoredEventGuidance: string;
  /** Ed 2026-09-29: a goal's carer guidance as ONE text (`_activityDefinition._carerGuidanceText`);
   *  seeded from the legacy 5 levels, composed, until one is saved. */
  guidanceText: string;
  attainment: string[];
  prompts: string[];
  /** Selected category code — supports task #7 "move to" */
  categoryCode: string;
  /** Selected aim code within that category */
  subcategoryCode: string;
  /** OMON-166: active D/M/S capability codes (trackable-capability codings). */
  capabilities: string[];
  /** OMON-166: `_recordingKind` (attainment-5 | rating | tally | boolean). */
  recordingKind: string;
  /** OMON-166 Part 2.5: FHIR ObservationDefinition.status — `active` | `retired`. */
  status: string;
  /** OMON-172: planner may set a reduction target per client (`_targetable`). */
  targetable: boolean;
  /** OMON-172: planner may escalate; surfaces the carer notify-office affordance (`_escalatable`). */
  escalatable: boolean;
  /** OMON-172: librarian default escalation threshold (`_escalationThresholdDefault`); null = unset. */
  escalationThreshold: number | null;
  /** OMON-172: carer can't close a day's entries without completing this (`_mandatory`).
   *  ADR-0035 am.8: now the derived shadow of `shiftRolesDefault` (owed by >=1 role). */
  mandatory: boolean;
  /** ADR-0035 am.8: the roles that owe this item by default (`_shiftRolesDefault`). */
  shiftRolesDefault: import("./shift-roles").ShiftRoleCode[];
  /** ADR-0035 am.8: planners can't change the role set (`_rolesLocked`). */
  rolesLocked: boolean;
}

export interface HierarchyInlineEdit {
  // OMON-166 Part 4: "fact" added — double-click a fact title to rename it in
  // place (code = the OD/AD doc id).
  kind: "category" | "aim" | "fact";
  code: string;
  categoryCode?: string;
}

export const state = {
  // ------------------------------------------------------------------
  // Catalogue + definitions — reassigned wholesale after each load
  // ------------------------------------------------------------------
  catalogueData: new Map<string, ObservationDefinition>(),
  activityData: new Map<string, ActivityDefinition>(),
  /** keyed by stable code */
  goalCategories: new Map<string, GoalCategoryNode>(),
  /** keyed by doc id: "{goalCategoryCode}__{goalSubcategoryCode}" */
  goalSubcategories: new Map<string, GoalSubcategoryNode>(),

  // ------------------------------------------------------------------
  // Patients + active-plan view
  // ------------------------------------------------------------------
  patients: [] as PatientInfo[],
  selectedPatient: null as PatientInfo | null,
  patientCarePlans: [] as CarePlanInfo[],
  patientGoals: [] as GoalInfo[],
  /**
   * OMON-119: monitored events attached to the currently-selected active
   * CarePlan, read from `_carePlan._monitoredEvents[]`. Distinct from
   * `patientGoals` because monitored events have different semantics
   * (three-state-ordinal, per-shift recording, not SOMA attainment).
   * Populated by `loadPatientPlan`; rendered as a separate section
   * in the patient detail view.
   */
  patientMonitoredEvents: [] as MonitoredEventInfo[],
  /**
   * OMON-149: client-context block for the plan-view header — age,
   * address summary, planned carers across the encounter window, and
   * today's working carers. Populated by `loadClientContext` in
   * parallel with `loadPatientPlan`. Null while loading or when no
   * patient is selected.
   */
  clientContext: null as ClientContextData | null,
  /** OMON-035: planner's draft (status:draft) for editor resume. */
  patientDraft: null as CarePlanInfo | null,
  /** OMON-035: docId of the in-flight draft (separate from patientDraft —
   *  patientDraft is null while a brand-new draft is being created and only
   *  set after the first autosave). */
  currentDraftId: null as string | null,
  /** OMON-035: debounce handle for the 1-second autosave timer. */
  autosaveDebounceTimer: null as number | null,
  listeners: [] as Unsubscribe[],
  // OMON-102: the catalogue live-listeners' handles now live in
  // loaders/ensure.ts (`ensureCatalogueLive` / `stopCatalogueLive`) — attached
  // once per session, detached at sign-out via resetEnsured().

  /**
   * OMON-121: active tab on the patient-detail view. Developmental vs
   * tasks (monitored events). Persists across renders within a session
   * but resets when a different patient is selected.
   */
  detailActiveTab: "developmental" as DetailActiveTab,

  // ------------------------------------------------------------------
  // Plan builder
  // ------------------------------------------------------------------
  builderGoals: [] as SelectedGoal[],
  /**
   * OMON-135: which builder rows have their carer-guidance editor
   * expanded. Stores builderGoals indices. Default-folded; the librarian's
   * 5-level editor is rendered only when the row is in this set. Reset
   * when the planner exits builder mode.
   */
  builderCarerGuidanceExpanded: new Set<number>(),
  /**
   * OMON-135: per-level edit/preview mode for builder rows. Keys are
   * `${rowIdx}::${levelIdx}` (level 0-4). When in the set, the level shows
   * the raw markdown textarea; when absent, the rendered foldable view
   * (`guidanceParser` + `guidanceRenderer`). Preview is the default
   * (Ed, 2026-09-24): guidance is read far more often than it is written.
   */
  builderCarerGuidanceEditing: new Set<string>(),
  /**
   * OMON-119 FULL: monitored events selected during plan creation.
   * Parallel stream to `builderGoals` — they're written to
   * `_carePlan._monitoredEvents[]` (per ADR-0015), not `_carePlan.goal[]`.
   * Cloned from `state.patientMonitoredEvents` when entering edit mode so
   * existing entries are preserved on edit.
   */
  builderMonitoredEvents: [] as BuilderMonitoredEvent[],
  builderMode: "view" as BuilderMode,
  // OMON-183 P4: Author | Shift preview lens in the plan builder. Preview
  // renders the draft as the carer iOS shift view will order it (mandatory
  // first, then the rest) — Ed's mandatory-vs-optional framing lives here,
  // never as the authoring structure.
  builderLens: "author" as "author" | "preview",
  /**
   * "Office contact for escalations" — the plan's FHIR R5 `custodian`, pinned
   * by a human. `null` means unpinned: whoever saves the plan becomes its
   * custodian (Ed's latest-saver default). The name is carried alongside the
   * id purely so the settings bar can render without a second lookup; the id
   * is what gets written. See clients/plan-custodian + clients/office-contact.
   */
  builderCustodian: null as { personDocId: string; name: string | null } | null,
  planMonths: 12,
  /** YYYY-MM-DD, backdatable */
  planStartDate: new Date().toISOString().slice(0, 10),

  // ------------------------------------------------------------------
  // Catalogue UI state
  // ------------------------------------------------------------------
  catalogueSelectedGoal: null as string | null,
  collapsedSections: new Set<string>(),
  chartCollapsedCategories: new Set<string>(),
  /**
   * OMON-070: picker collapsible sections (the catalogue browser shown
   * when adding objectives to a plan). Independent from
   * `collapsedSections` (catalogue tab) so each surface keeps its own
   * fold state. Keys are namespaced: `cat:<catCode>` for category
   * headers, `subcategory:<catCode>__<subcategoryCode>` for aim sub-headers, so the
   * two levels can't collide.
   */
  pickerCollapsedSections: new Set<string>(),
  catalogueEditMode: false,
  /**
   * OMON-068 follow-up (2026-05-10): when in edit mode AND on the
   * Carer Guidance Prompts tab, the librarian can flip between raw
   * markdown textareas (false) and rendered fold preview (true)
   * without committing or cancelling. Live textarea content is
   * captured into editingDetail before the flip so typing isn't lost.
   * Reset to false on entering or leaving edit mode.
   */
  catalogueEditPreview: false,
  catalogueDetailTab: "attainment" as CatalogueDetailTab,
  catalogueMonitoredTab: "prompt" as CatalogueMonitoredTab,
  editingDetail: null as EditingDetail | null,
  showingAddCustom: false,

  // ------------------------------------------------------------------
  // Hierarchy edit mode — librarian controls for category/aim CRUD
  // ------------------------------------------------------------------
  /** Only one inline rename can be active at a time */
  // 2026-06-11 (Ed): the two-pane hierarchy editor IS the Goals Inventory —
  // the legacy full-page drill-in (view mode + Edit/Delete buttons) is gone.
  hierarchyEditMode: true,
  hierarchyInlineEdit: null as HierarchyInlineEdit | null,
  /** Tracks which category shows the "add aim" inline form */
  hierarchyAddSubcategoryIn: null as string | null,
  /**
   * OMON-171: the PARENT fact name whose inline "add variant" draft row is
   * open (a focused, empty title input rendered inside that parent's group).
   * Mirrors `hierarchyAddSubcategoryIn` one tier down. Null = no draft open.
   * Commit on Enter / non-blank blur creates the variant; blank blur / Escape
   * abandons it (no doc written).
   */
  hierarchyAddFactIn: null as string | null,
  hierarchyAddVariantIn: null as string | null,
  /** True when the "add category" inline form is visible at the bottom of the tree */
  hierarchyShowAddCategory: false,
  /**
   * When true, retired hierarchy nodes are shown (dimmed) so librarians
   * can restore them. When false (default), retired nodes are hidden
   * even in edit mode.
   */
  hierarchyShowRetired: false,
  /**
   * OMON-166 Part 3: true while a catalogue drag-to-recategorise is in
   * flight. The live `onSnapshot` catalogue listeners refresh the data Map
   * but SUPPRESS `renderCatalogue()` while this is set — a mid-drag re-render
   * would destroy the dragged DOM node out from under SortableJS (including
   * the re-render our own recategorise write triggers). Reset in Sortable
   * `onEnd`, which always fires after a drag.
   */
  catalogueDragging: false,
  /**
   * OMON-079 79f: librarian's "still has placeholder text" filter.
   * When true, the catalogue tree only shows goals whose
   * `_activityDefinition.documentation[].display` still carries the
   * post-reseed placeholder marker. Surfaces Jo's authoring queue.
   */
  hierarchyShowOnlyPlaceholder: false,
  /**
   * OMON-167: capability lens for the goals inventory. Holds any of the
   * Trackable capability codes ("developmental" | "monitored" | "safety").
   * Empty set = show all (current behaviour). Multi-select = show facts
   * carrying ANY selected capability (OR within the filter), AND-combined
   * with the search box. Session-scoped (in-memory, survives re-renders and
   * navigation away/back) — deliberately NOT persisted to LocalStorage like
   * fold state, so a stale lens never silently narrows a fresh visit.
   */
  catalogueCapabilityFilter: new Set<string>(),

  /**
   * ADR-0048 phase 2: the provenance lens — "all" (default), "training"
   * (only items promoted from training) or "local" (only what production
   * authored itself).
   *
   * Session-scoped for the same reason as the capability filter above: a
   * lens that silently persisted across visits would make a librarian
   * believe the catalogue is smaller than it is. The default hides nothing.
   */
  catalogueProvenanceFilter: "all" as ProvenanceFilter,

  /**
   * OMON-068: per-goal carer-guidance fold state for the librarian's
   * detail view. Keyed by goal name (= `_activityDefinition` doc id);
   * value is the set of `level-N::sectionSlug` keys the librarian has
   * explicitly **collapsed**. Empty Set ⇒ all sections expanded
   * (default for librarian view; carer iOS app uses the inverse
   * default). Session-only — not persisted to Firestore. Reset on
   * page reload.
   */
  guidanceFoldState: new Map<string, Set<string>>(),

  // ------------------------------------------------------------------
  // Chart.js instances — destroyed on re-render to avoid canvas reuse errors
  // ------------------------------------------------------------------
  activeCharts: [] as Chart[],

  // ------------------------------------------------------------------
  // Actual observations (loaded per patient) — enriched with metadata for chart tooltips
  // ------------------------------------------------------------------
  patientActuals: {} as Record<string, ActualObservation[]>,
  /** OMON-129: the Combined-narrative tab's entries (both streams, plan period). */
  patientNarrative: [] as NarrativeEntry[],

  // ------------------------------------------------------------------
  // OMON-137: admin auth claims + admin-surface UI state.
  //
  // `authClaims` is populated by auth.ts after sign-in (force-refresh).
  // Null means either not-yet-loaded, sign-out, or fail-closed (the
  // claims didn't match the expected shape). All admin renderers must
  // re-check on every render — don't cache derived booleans.
  //
  // `adminPanel` is which admin sub-screen is active, or null when the
  // user is on one of the regular tabs. Used by navigation.ts to keep
  // the hamburger dropdown in sync with the visible panel.
  // ------------------------------------------------------------------
  authClaims: null as AuthClaims | null,
  adminPanel: null as "users-roles" | "audit-log" | "access-requests" | null,
};
